import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, patch } from "@/lib/api";
import { FieldError } from "./States";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fromAddress = (a) => ({ lock_type: a.lock_type || "", key_safe_code: a.key_safe_code || "", no_key_safe: !!a.no_key_safe, phone_left: !!a.phone_left, key_return_phone: a.key_return_phone || "" });

const Check = ({ id, checked, onChange, label }) => (
  <div className="flex items-center gap-2">
    <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} data-testid={id} />
    <Label htmlFor={id} className="cursor-pointer font-medium">{label}</Label>
  </div>
);

export const LockChangeDialog = ({ open, onOpenChange, address, onSaved }) => {
  const [v, setV] = useState(fromAddress(address));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (open) {
      setV(fromAddress(address));
      setError("");
    }
  }, [open, address]);

  const send = async (body, kind, msg) => {
    setBusy(kind);
    try {
      onSaved(await patch(`/addresses/${address.id}`, body));
      toast.success(msg);
      onOpenChange(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  const save = (e) => {
    e.preventDefault();
    if (!v.lock_type.trim()) return setError("Enter the lock type");
    const phoneLeft = v.phone_left;
    if (phoneLeft && !v.key_return_phone.trim()) return setError("Enter the telephone number left for key return");
    send({
      lock_changed: true, lock_type: v.lock_type.trim(), no_key_safe: v.no_key_safe,
      key_safe_code: v.no_key_safe ? null : v.key_safe_code.trim() || null, phone_left: phoneLeft,
      key_return_phone: phoneLeft ? v.key_return_phone.trim() : null,
    }, "save", "Lock change saved");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md" data-testid="lock-change-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{address.lock_changed ? "Edit lock change" : "Lock change"}</DialogTitle>
          <DialogDescription>{address.full_address} · {address.postcode}</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} noValidate className="space-y-4">
          <div>
            <Label htmlFor="lc-type">Lock type *</Label>
            <Input id="lc-type" value={v.lock_type} onChange={(e) => { setV({ ...v, lock_type: e.target.value }); setError(""); }} maxLength={200} placeholder="e.g. Euro cylinder" className="mt-1.5 h-11" data-testid="lock-change-type-input" />
          </div>
          <Check id="lock-change-no-key-safe" checked={v.no_key_safe} onChange={(c) => setV({ ...v, no_key_safe: c })} label="No key safe" />
          {!v.no_key_safe && (
            <div>
              <Label htmlFor="lc-code">Key Safe Code</Label>
              <Input id="lc-code" value={v.key_safe_code} onChange={(e) => setV({ ...v, key_safe_code: e.target.value })} maxLength={100} autoComplete="off" className="font-data mt-1.5 h-11" data-testid="lock-change-code-input" />
            </div>
          )}
          <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <Check id="lock-change-phone-left" checked={v.phone_left} onChange={(c) => setV({ ...v, phone_left: c })} label="I've left a telephone number for key return" />
            {v.phone_left && (
              <div>
                <Label htmlFor="lc-phone">Telephone number left *</Label>
                <Input id="lc-phone" type="tel" inputMode="tel" value={v.key_return_phone} maxLength={30} placeholder="e.g. 07700 900123" className="font-data mt-1.5 h-11 bg-white"
                  onChange={(e) => { setV({ ...v, key_return_phone: e.target.value }); setError(""); }} data-testid="lock-change-phone-input" />
              </div>
            )}
          </div>
          <FieldError message={error} testId="lock-change-error" />
          <DialogFooter className="gap-2 sm:justify-between">
            {address.lock_changed ? (
              <Button type="button" variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" disabled={!!busy}
                onClick={() => send({ lock_changed: false }, "remove", "Lock change removed")} data-testid="lock-change-remove-button">
                {busy === "remove" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Remove lock change
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={!!busy} data-testid="lock-change-cancel-button">Cancel</Button>
              <Button type="submit" disabled={!!busy} className="bg-blue-700 hover:bg-blue-800" data-testid="lock-change-save-button">
                {busy === "save" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
